import { useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { ATLAS_LENS_COLORS, resolveAtlasLensColors } from '../Form/formFieldSpec';
import { ATLAS_LENS_COMPONENT_VARS, ATLAS_LENS_SEMANTIC_VARS } from '../Form/atlasLensTokens';

// ——— Spacing ———

export const LENS_CORE_SPACING_VARS = {
  '0': 'var(--lens-core-spacing-0)',
  '0_25': 'var(--lens-core-spacing-0-25)',
  '0_5': 'var(--lens-core-spacing-0-5)',
  '1': 'var(--lens-core-spacing-1)',
  '1_5': 'var(--lens-core-spacing-1-5)',
  '2': 'var(--lens-core-spacing-2)',
  '2_5': 'var(--lens-core-spacing-2-5)',
  '3': 'var(--lens-core-spacing-3)',
  '4': 'var(--lens-core-spacing-4)',
  '4_5': 'var(--lens-core-spacing-4-5)',
  '5': 'var(--lens-core-spacing-5)',
  '6': 'var(--lens-core-spacing-6)',
  '7': 'var(--lens-core-spacing-7)',
  '8': 'var(--lens-core-spacing-8)',
  '9': 'var(--lens-core-spacing-9)',
  px: 'var(--lens-core-spacing-px)',
} as const;

export type LensCoreSpacingKey = keyof typeof LENS_CORE_SPACING_VARS;

const LENS_SPACING_PX_FALLBACK: Record<LensCoreSpacingKey, number> = {
  '0': 0,
  '0_25': 2,
  '0_5': 4,
  '1': 8,
  '1_5': 12,
  '2': 16,
  '2_5': 20,
  '3': 24,
  '4': 32,
  '4_5': 36,
  '5': 40,
  '6': 48,
  '7': 56,
  '8': 64,
  '9': 72,
  px: 1,
};

export function lensSpacingPx(tokens: LensTheme['tokens'], key: LensCoreSpacingKey): number {
  const themed = tokens?.core?.spacing?.[key as keyof typeof tokens.core.spacing];
  if (themed?.value) {
    return parseFloat(themed.value);
  }
  return LENS_SPACING_PX_FALLBACK[key];
}

export function lensSpacing(tokens: LensTheme['tokens'], key: LensCoreSpacingKey): string {
  const themed = tokens?.core?.spacing?.[key as keyof typeof tokens.core.spacing];
  return themed?.value ?? LENS_CORE_SPACING_VARS[key];
}

function toPx(value: number) {
  return `${value}px`;
}

function s(tokens: LensTheme['tokens'], key: LensCoreSpacingKey) {
  return lensSpacing(tokens, key);
}

// ——— Typography ———

type LensFontToken = {
  fontSize: { value: string };
  lineHeight: { value: string };
  fontWeight?: { value: number | string };
  letterSpacing?: { value: string };
};

export function lensTypographySx(
  font: LensFontToken | undefined,
  options?: { fontWeight?: string | number; color?: string },
) {
  return {
    fontSize: font?.fontSize?.value ?? '14px',
    lineHeight: font?.lineHeight?.value ?? '20px',
    ...(font?.fontWeight != null && { fontWeight: font.fontWeight.value }),
    ...(font?.letterSpacing != null && { letterSpacing: font.letterSpacing.value }),
    ...options,
  };
}

export function onboardingTypography(tokens: LensTheme['tokens']) {
  const font = tokens.semantic?.font;
  const emphasis = tokens.semantic?.fontWeight?.emphasis?.value ?? 600;
  const numberSmFont =
    (font as { number?: { sm: LensFontToken } } | undefined)?.number?.sm ?? font?.text?.sm;

  return {
    h2Display: lensTypographySx(font?.title?.h2Display, { fontWeight: emphasis }),
    h1Billboard: lensTypographySx(font?.title?.h1Billboard, { fontWeight: emphasis }),
    h4Md: lensTypographySx(font?.title?.h4Md, { fontWeight: emphasis }),
    textMd: lensTypographySx(font?.text?.md),
    textMdEmphasis: lensTypographySx(font?.text?.md, { fontWeight: emphasis }),
    textSm: lensTypographySx(font?.text?.sm),
    textSmEmphasis: lensTypographySx(font?.text?.sm, { fontWeight: emphasis }),
    labelEmphasis: lensTypographySx(font?.text?.sm, { fontWeight: emphasis }),
    labelRequired: lensTypographySx(font?.text?.sm),
    body: lensTypographySx(font?.text?.body),
    bodyEmphasis: lensTypographySx(font?.text?.body, { fontWeight: emphasis }),
    h3LgEmphasis: lensTypographySx(font?.title?.h3Lg, { fontWeight: emphasis }),
    numberSm: lensTypographySx(numberSmFont),
    button: lensTypographySx(font?.text?.body, { fontWeight: emphasis }),
  };
}

// ——— Focus & button styles ———

export function lensButtonFocusSx(tokens: LensTheme['tokens']) {
  return {
    outline: 'none',
    '&:focus': { outline: 'none' },
    '&:focus:not(:focus-visible)': { boxShadow: 'none' },
    '&.Mui-focusVisible': {
      outline: 'none',
      boxShadow:
        tokens.semantic?.shadow?.focus?.value?.stringValue ?? ATLAS_LENS_COLORS.focusRing,
    },
  } as const;
}

export function lensFocusRingGutter(tokens: LensTheme['tokens']): string {
  const layers = tokens.semantic?.shadow?.focus?.value?.layers ?? [];
  let maxSpreadPx = 0;
  for (const layer of layers) {
    if (layer?.spread == null) {
      continue;
    }
    maxSpreadPx = Math.max(maxSpreadPx, parseFloat(String(layer.spread)));
  }
  if (maxSpreadPx > 0) {
    // Buffer beyond spread so the ring is not clipped at overflow-x edges (subpixel + border).
    return `${maxSpreadPx + lensSpacingPx(tokens, '0_25')}px`;
  }
  return lensSpacing(tokens, '0_5');
}

export function lensSecondaryButtonSx(tokens: LensTheme['tokens']) {
  const c = ATLAS_LENS_COMPONENT_VARS;
  return {
    minHeight: c.buttonSmallHeight,
    px: c.buttonSmallPaddingX,
    gap: c.buttonSmallGap,
    borderRadius: c.buttonSmallBorderRadius,
    borderColor: c.buttonSecondaryDefaultBorder,
    backgroundColor: c.buttonSecondaryDefaultBackground,
    color: c.buttonSecondaryDefaultColor,
    textTransform: 'none' as const,
    fontSize: c.buttonSmallFontSize,
    lineHeight: c.buttonSmallLineHeight,
    fontWeight: c.buttonSmallFontWeight,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: c.buttonSecondaryHoverBackground,
      borderColor: c.buttonSecondaryHoverBorder,
      boxShadow: 'none',
    },
    ...lensButtonFocusSx(tokens),
  };
}

/** Atlas Lens Announcement banner (Figma 17786:17464). Token paths verified via Atlas MCP `search_atlas_tokens_by_path`. */
export function onboardingCoursesBannerLens(tokens: LensTheme['tokens']) {
  const radius = tokens.semantic?.radius;
  const bannerBg =
    tokens.semantic?.color?.background?.container?.value ??
    ATLAS_LENS_COLORS.backgroundContainer;
  const iconTileBg =
    tokens.semantic?.color?.background?.navSelection?.value ??
    ATLAS_LENS_COLORS.backgroundNavSelection;
  const typeColor =
    tokens.semantic?.color?.type?.default?.value ?? ATLAS_LENS_COLORS.typeDefault;
  const shellPadding = lensSpacing(tokens, '2');
  const shellGap = lensSpacing(tokens, '2');
  const contentMinHeight = lensSpacing(tokens, '9');
  const shellMinHeightPx =
    lensSpacingPx(tokens, '2') * 2 + lensSpacingPx(tokens, '9');

  return {
    bannerBg,
    shell: {
      padding: shellPadding,
      gap: shellGap,
      minHeight: toPx(shellMinHeightPx),
      borderRadius: radius?.lg?.value ?? 'var(--lens-semantic-radius-lg)',
    },
    iconTile: {
      size: lensSpacing(tokens, '5'),
      padding: lensSpacing(tokens, '1'),
      borderRadius: radius?.md?.value ?? 'var(--lens-semantic-radius-md)',
      glyphSizePx: lensSpacingPx(tokens, '3'),
      background: iconTileBg,
      color: tokens.semantic?.color?.type?.inverse?.value ?? ATLAS_LENS_COLORS.typeInverse,
    },
    content: {
      minHeight: contentMinHeight,
      height: contentMinHeight,
    },
    details: {
      gap: lensSpacing(tokens, '0_5'),
      maxWidth: 570,
      minHeight: contentMinHeight,
      descriptionMaxHeight: toPx(lensSpacingPx(tokens, '5')),
    },
    titleFontWeight: tokens.semantic?.fontWeight?.emphasis?.value ?? 600,
    typeColor,
    containerSize: 'sm' as const,
    buttonOnBanner: {
      minWidth: toPx(153),
      backgroundColor: bannerBg,
      borderColor: typeColor,
      color: typeColor,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: bannerBg,
        borderColor: typeColor,
        boxShadow: 'none',
      },
    },
  };
}

/** Legacy sample preview Announcement (Figma 17247:90642). */
export function legacySampleCoursesBannerLens(tokens: LensTheme['tokens']) {
  const radius = tokens.semantic?.radius;
  const bannerBg =
    tokens.semantic?.color?.surface?.variant?.value ?? ATLAS_LENS_COLORS.surfaceVariant;
  const iconTileBg =
    tokens.semantic?.color?.background?.navSelection?.value ??
    ATLAS_LENS_COLORS.backgroundNavSelection;
  const typeColor =
    tokens.semantic?.color?.type?.default?.value ?? ATLAS_LENS_COLORS.typeDefault;
  const shellPadding = lensSpacing(tokens, '2');
  const shellGap = lensSpacing(tokens, '2');

  return {
    bannerBg,
    shell: {
      padding: shellPadding,
      gap: shellGap,
      minHeight: 'auto',
      borderRadius: radius?.lg?.value ?? 'var(--lens-semantic-radius-lg)',
    },
    iconTile: {
      size: lensSpacing(tokens, '5'),
      padding: lensSpacing(tokens, '1'),
      borderRadius: radius?.md?.value ?? 'var(--lens-semantic-radius-md)',
      glyphSizePx: lensSpacingPx(tokens, '3'),
      background: iconTileBg,
      color: tokens.semantic?.color?.type?.inverse?.value ?? ATLAS_LENS_COLORS.typeInverse,
    },
    content: {
      minHeight: 'auto',
      height: 'auto',
    },
    details: {
      gap: lensSpacing(tokens, '0_5'),
      maxWidth: 570,
      minHeight: 'auto',
      descriptionMaxHeight: 'none',
    },
    titleFontWeight: tokens.semantic?.fontWeight?.emphasis?.value ?? 600,
    typeColor,
    containerSize: 'sm' as const,
    buttonOnBanner: {
      minWidth: toPx(130),
      backgroundColor: bannerBg,
      borderColor: typeColor,
      color: typeColor,
      boxShadow: 'none',
      '&:hover': {
        backgroundColor: bannerBg,
        borderColor: typeColor,
        boxShadow: 'none',
      },
    },
  };
}

// ——— Wizard layout ———

export function onboardingLensLayout(tokens: LensTheme['tokens']) {
  const radius = tokens.semantic?.radius;
  const modal = tokens.component?.modal;

  const dialogWidthPx = lensSpacingPx(tokens, '6') * 16 + lensSpacingPx(tokens, '4');
  const dialogHeightPx = lensSpacingPx(tokens, '8') * 10;
  const footerHeightPx = lensSpacingPx(tokens, '7');
  const dialogPaddingBottomPx = lensSpacingPx(tokens, '2');
  const dialogMarginPx = lensSpacingPx(tokens, '2');
  const contentHeightPx =
    dialogHeightPx - footerHeightPx - dialogPaddingBottomPx - lensSpacingPx(tokens, 'px');
  const leftColumnWidthPx = lensSpacingPx(tokens, '5') * 10;
  const illustrationHeightPx = lensSpacingPx(tokens, '5') * 10;
  const illustrationWidthPx =
    leftColumnWidthPx +
    lensSpacingPx(tokens, '6') * 5 +
    lensSpacingPx(tokens, '0_5') +
    lensSpacingPx(tokens, 'px') * 2;

  return {
    dialog: {
      width: toPx(dialogWidthPx),
      height: toPx(dialogHeightPx),
      borderRadius:
        modal?.borderRadius?.value ?? radius?.lg?.value ?? 'var(--lens-component-modal-border-radius)',
      shadow: ATLAS_LENS_COMPONENT_VARS.modalBoxShadow,
      paddingBottom: toPx(dialogPaddingBottomPx),
      margin: toPx(dialogMarginPx),
    },
    content: {
      height: toPx(contentHeightPx),
      paddingBottom: s(tokens, '3'),
      viewportMinHeight: toPx(contentHeightPx),
    },
    formSection: {
      paddingX: s(tokens, '6'),
      paddingY: s(tokens, '5'),
      paddingYCompact: s(tokens, '4'),
      sectionGap: s(tokens, '3'),
      headerSubtitleGap: s(tokens, '1'),
      /** Figma page header — space below subtitle before step content (12px). */
      headerSubtitleBottomSpacing: s(tokens, '1_5'),
    },
    formField: {
      labelGap: s(tokens, '0_5'),
      labelToInputGap: s(tokens, '1'),
      labelRowHeight: s(tokens, '2'),
      inputHeight: s(tokens, '5'),
      inputPaddingX: s(tokens, '1_5'),
      inputPaddingY: s(tokens, '1'),
      inputRadius: radius?.md?.value ?? 'var(--lens-semantic-radius-md)',
      totalHeight: s(tokens, '8'),
      selectIconGutter: s(tokens, '4'),
    },
    dropdownRow: {
      gap: s(tokens, '3'),
      fieldMinWidth: `calc(${s(tokens, '6')} * 7 + ${s(tokens, '4')})`,
    },
    regions: {
      labelHeight: s(tokens, '2'),
      columnGap: s(tokens, '3'),
      checkboxGap: s(tokens, '1_5'),
      rowPaddingY: s(tokens, '1_5'),
      checkboxSize: s(tokens, '3'),
      checkboxRadius: radius?.sm?.value ?? 'var(--lens-semantic-radius-sm)',
    },
    optionCard: {
      listGap: s(tokens, '2'),
      paddingX: s(tokens, '1_5'),
      paddingY: s(tokens, '1_5'),
      minHeight: `calc(${s(tokens, '4_5')} + ${s(tokens, '4')})`,
      borderRadius: radius?.md?.value ?? 'var(--lens-semantic-radius-md)',
      radioGap: s(tokens, '2'),
      labelGap: s(tokens, '0_5'),
      radioSize: s(tokens, '3'),
      radioIndicatorSize: s(tokens, '1'),
      radioBorderWidth: s(tokens, 'px'),
    },
    multiSelect: {
      maxVisibleLabels: 4,
      selectIconGutter: s(tokens, '4'),
    },
    textArea: {
      labelRowHeight: s(tokens, '2'),
      minHeight: `calc(${s(tokens, '6')} + ${s(tokens, '4_5')})`,
      contentMinHeight: s(tokens, '6'),
      paddingX: s(tokens, '1_5'),
      paddingY: s(tokens, '1'),
      borderRadius: radius?.md?.value ?? 'var(--lens-semantic-radius-md)',
    },
    welcome: {
      leftColumnWidth: toPx(leftColumnWidthPx),
      leftColumnPaddingX: s(tokens, '4'),
      leftColumnSectionGap: s(tokens, '6'),
      leftColumnContentWidth: toPx(leftColumnWidthPx - lensSpacingPx(tokens, '4') * 2),
      formFieldsGap: s(tokens, '3'),
    },
    reassign: {
      leftColumnWidth: toPx(leftColumnWidthPx),
      leftColumnPaddingX: s(tokens, '4'),
      leftColumnPaddingY: s(tokens, '5'),
      leftColumnContentWidth: toPx(leftColumnWidthPx - lensSpacingPx(tokens, '4') * 2),
      leftColumnSectionGap: s(tokens, '3'),
    },
    brand: {
      radioHeight: s(tokens, '4'),
      radioSize: s(tokens, '3'),
      radioBorderWidth: s(tokens, 'px'),
      radioIndicatorSize: s(tokens, '1'),
      radioOptionPaddingY: s(tokens, '0_5'),
      radioGap: s(tokens, '3'),
      radioLabelGap: s(tokens, '1_5'),
      filesRowHeight: `calc(${s(tokens, '8')} + ${s(tokens, '4_5')})`,
      thumbnailSize: s(tokens, '8'),
      filesContentPadding: s(tokens, '1_5'),
      filesGapToAttach: s(tokens, '1_5'),
      colorPanelPaddingX: toPx(lensSpacingPx(tokens, '3')),
      colorPanelPaddingTop: toPx(lensSpacingPx(tokens, '3')),
      colorPanelPaddingBottom: toPx(lensSpacingPx(tokens, '8')),
      colorPanelBorderRadius: radius?.lg?.value ?? 'var(--lens-semantic-radius-lg)',
      colorPanelColumnGap: s(tokens, '3'),
      colorPanelColumnWidth: `calc(${s(tokens, '6')} * 7 + ${s(tokens, '4')})`,
      colorPanelFrameMaxWidth: toPx(dialogWidthPx - lensSpacingPx(tokens, '6') * 2),
      colorPanelSectionHeaderHeight: `calc(${s(tokens, '5')} + ${s(tokens, '0_5')})`,
      colorPanelFormFieldHeight: s(tokens, '8'),
      colorPanelSectionTitleGap: s(tokens, '1'),
      colorPanelFormFieldsGap: s(tokens, '3'),
      colorPanelSwatchGap: s(tokens, '1_5'),
      colorPanelSwatchSize: s(tokens, '2_5'),
    },
    loading: {
      spinnerSize: s(tokens, '5'),
      spinnerGap: s(tokens, '3'),
      contentGap: s(tokens, '1'),
      messageGap: s(tokens, '2'),
      blockMaxWidth: `calc(${s(tokens, '5')} * 9)`,
      subtitleMaxWidth: `calc(${s(tokens, '5')} * 6 + ${s(tokens, '3')})`,
    },
    complete: {
      emptyStatePaddingY: s(tokens, '1_5'),
      emptyStateMaxWidth: `calc(${s(tokens, '5')} * 17 + ${s(tokens, '4')})`,
      /** Figma empty-state illustration 160×132 — same formula as EmptyState / emptyStateLens. */
      illustrationWidth: `calc(${s(tokens, '8')} * 2 + ${s(tokens, '4')})`,
      illustrationHeight: `calc(${s(tokens, '8')} * 2 + ${s(tokens, '1')})`,
      contentGap: s(tokens, '1'),
      illustrationGap: s(tokens, '6'),
      primaryButtonMinWidth: `calc(${s(tokens, '7')} * 3 + ${s(tokens, '4')})`,
    },
    illustration: {
      width: toPx(illustrationWidthPx),
      height: toPx(illustrationHeightPx),
      left: toPx(leftColumnWidthPx),
      widthRatio: illustrationWidthPx / dialogWidthPx,
      aspectRatio: `${illustrationWidthPx} / ${illustrationHeightPx}`,
      screenInsetTop: '13.4%',
      screenInsetRight: '13.86%',
      screenInsetBottom: '13.4%',
      screenInsetLeft: '13.86%',
    },
    footer: {
      height: toPx(footerHeightPx),
      paddingX: s(tokens, '6'),
      paddingTop: s(tokens, '2'),
      paddingBottom: s(tokens, '0'),
      dotStepperTop: toPx(lensSpacingPx(tokens, '2') + lensSpacingPx(tokens, '1_5') + lensSpacingPx(tokens, 'px')),
      buttonHeight: s(tokens, '5'),
      buttonMinWidth: `calc(${s(tokens, '6')} + ${s(tokens, '7')})`,
      finishButtonMinWidth: `calc(${s(tokens, '7')} * 2 + ${s(tokens, '4')})`,
      reassignDoneMinWidth: `calc(${s(tokens, '5')} + ${s(tokens, '1_5')})`,
      backButtonMinWidth: s(tokens, '9'),
      stepperGap: s(tokens, '1'),
      dotSize: s(tokens, '1_5'),
      actionGap: s(tokens, '1_5'),
      contentMinHeight: s(tokens, '5'),
      skipButtonRadius: radius?.lg?.value ?? 'var(--lens-semantic-radius-lg)',
      skipButtonPaddingX: s(tokens, '1_5'),
      skipButtonPaddingY: s(tokens, '1'),
      menuMinWidth: `calc(${s(tokens, '7')} * 3)`,
      menuBorderRadius: radius?.md?.value ?? 'var(--lens-semantic-radius-md)',
      menuShadow: ATLAS_LENS_COMPONENT_VARS.menuBoxShadow,
      menuOffset: s(tokens, '0_5'),
    },
    icon: {
      md: s(tokens, '3'),
      lg: tokens.semantic.iconSize?.lg?.value ?? 'var(--lens-semantic-icon-size-lg)',
    },
    focusRingGutter: lensFocusRingGutter(tokens),
    welcomeStep1: onboardingWelcomeStep1Lens(tokens, {
      contentHeightPx,
      leftColumnWidthPx,
      illustrationWidthPx,
      illustrationHeightPx,
      footerHeightPx,
    }),
    scrollbar: onboardingScrollbarLens(tokens),
  };
}

type WelcomeStep1LayoutDeps = {
  contentHeightPx: number;
  leftColumnWidthPx: number;
  illustrationWidthPx: number;
  illustrationHeightPx: number;
  footerHeightPx: number;
};

/** CEE-992 — Welcome step (AI opt-in) layout from Atlas Lens spacing, radius, and color tokens. */
function onboardingWelcomeStep1Lens(
  tokens: LensTheme['tokens'],
  deps: WelcomeStep1LayoutDeps,
) {
  const radius = tokens.semantic?.radius;
  const accentBlue = tokens.semantic?.color?.accent?.blue;
  const h1LineHeightPx = parseFloat(
    tokens.semantic?.font?.title?.h1Billboard?.lineHeight?.value ?? `${lensSpacingPx(tokens, '4') + lensSpacingPx(tokens, '0_5')}`,
  );
  const pageHeaderHeightPx = lensSpacingPx(tokens, '5') + h1LineHeightPx;
  const contentRowHeightPx = deps.contentHeightPx - pageHeaderHeightPx;
  const dotSizePx = lensSpacingPx(tokens, '1_5');
  const stepperGapPx = lensSpacingPx(tokens, '1');
  const stepperDotCount = 7;

  return {
    pageHeader: {
      paddingTop: s(tokens, '5'),
      paddingX: s(tokens, '4'),
      height: toPx(pageHeaderHeightPx),
    },
    contentRow: {
      height: toPx(contentRowHeightPx),
    },
    leftColumn: {
      width: toPx(deps.leftColumnWidthPx),
      paddingX: s(tokens, '4'),
      contentWidth: toPx(deps.leftColumnWidthPx - lensSpacingPx(tokens, '4') * 2),
      sectionGap: s(tokens, '3'),
      titleSubtitleGap: s(tokens, '1'),
    },
    alert: {
      paddingY: s(tokens, '1'),
      paddingX: s(tokens, '2'),
      iconGap: s(tokens, '1_5'),
      borderRadius: radius?.lg?.value ?? 'var(--lens-semantic-radius-lg)',
      iconSize: s(tokens, '3'),
      messagePaddingY: s(tokens, '0_5'),
      backgroundColor:
        accentBlue?.background?.value ?? ATLAS_LENS_SEMANTIC_VARS.accentBlueBackground,
      contentColor: accentBlue?.content?.value ?? ATLAS_LENS_SEMANTIC_VARS.accentBlueContent,
    },
    illustration: {
      width: toPx(deps.illustrationWidthPx),
      height: toPx(deps.illustrationHeightPx),
      left: toPx(deps.leftColumnWidthPx),
    },
    stepper: {
      dotSize: toPx(dotSizePx),
      gap: s(tokens, '1'),
      width: toPx(stepperDotCount * dotSizePx + (stepperDotCount - 1) * stepperGapPx),
      top: toPx(lensSpacingPx(tokens, '2') + lensSpacingPx(tokens, '1_5') + lensSpacingPx(tokens, 'px')),
    },
    footer: {
      height: toPx(deps.footerHeightPx),
      paddingX: s(tokens, '4'),
      paddingTop: s(tokens, '2'),
      rowGap: s(tokens, '1'),
      actionGap: s(tokens, '1_5'),
      skipButtonMinWidth: `calc(${s(tokens, '5')} * 4 + ${s(tokens, '1_5')})`,
      setupWithoutAiMinWidth: `calc(${s(tokens, '5')} * 4 + ${s(tokens, '1_5')} - ${s(tokens, '0_5')})`,
      setupWithAiMinWidth: `calc(${s(tokens, '5')} * 3 + ${s(tokens, '2')})`,
      buttonHeight: s(tokens, '5'),
      buttonRadius: radius?.lg?.value ?? 'var(--lens-semantic-radius-lg)',
      buttonPaddingX: s(tokens, '1_5'),
      buttonPaddingY: s(tokens, '1'),
    },
  };
}

/** Custom scrollbar track + thumb — semantic ui.scrollbar + core spacing (no hardcoded hex/px). */
function onboardingScrollbarLens(tokens: LensTheme['tokens']) {
  const radius = tokens.semantic?.radius;
  const divider =
    tokens.semantic?.color?.ui?.divider?.default?.value ?? ATLAS_LENS_COLORS.divider;
  const surfaceSubtle =
    tokens.semantic?.color?.surface?.subtle?.value ?? ATLAS_LENS_COLORS.surfaceSubtle;
  const surfaceVariant =
    tokens.semantic?.color?.surface?.variant?.value ?? ATLAS_LENS_COLORS.surfaceVariant;

  const trackWidthPx = lensSpacingPx(tokens, '2') + lensSpacingPx(tokens, 'px');
  const thumbWidthPx = lensSpacingPx(tokens, '1') + lensSpacingPx(tokens, 'px');
  const thumbMinHeightPx = lensSpacingPx(tokens, '3') + lensSpacingPx(tokens, 'px');
  const thumbOffsetTopPx = lensSpacingPx(tokens, 'px');
  const thumbBorderRadius = radius?.sm?.value ?? s(tokens, '0_5');

  return {
    track: {
      width: trackWidthPx,
      widthCss: toPx(trackWidthPx),
      backgroundColor: surfaceSubtle,
      insetShadow: `inset ${s(tokens, 'px')} 0 0 ${divider}, inset -${s(tokens, 'px')} 0 0 ${surfaceVariant}`,
    },
    thumb: {
      width: thumbWidthPx,
      widthCss: toPx(thumbWidthPx),
      minHeight: thumbMinHeightPx,
      borderRadius: thumbBorderRadius,
      offsetTop: thumbOffsetTopPx,
      color: ATLAS_LENS_SEMANTIC_VARS.scrollbarHandle,
      centerOffset: `calc(50% - ${toPx(thumbWidthPx)} / 2 + ${s(tokens, 'px')})`,
    },
  };
}

// ——— Hooks ———

export function useLensTheme(): Theme & LensTheme {
  return useTheme() as Theme & LensTheme;
}

export function useOnboardingLens() {
  const theme = useLensTheme();
  const { tokens } = theme;
  const layout = onboardingLensLayout(tokens);
  const typography = onboardingTypography(tokens);
  const resolvedColors = resolveAtlasLensColors({ tokens });

  return {
    tokens,
    layout,
    typography,
    colors: ATLAS_LENS_COLORS,
    ...resolvedColors,
  };
}
